import { useState, type CSSProperties } from 'react';
import { DiffVaderConfigSchema } from '@death-star/shared';
import { ApiError, apiSendJson } from '../api-client.js';
import { useAuth } from '../auth.js';
import type { DvMode, DvConfigShape } from './InheritanceBadge.js';

interface OrgEnablePrerequisitesProps {
  orgName: string;
  targetMode: 'shadow' | 'enabled';
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
}

function OrgEnablePrerequisites({ orgName, targetMode, onConfirm, onCancel, saving }: OrgEnablePrerequisitesProps) {
  const [confirmed, setConfirmed] = useState(false);

  const panelStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--loop-space-150)',
  };

  const codeStyle: CSSProperties = {
    fontFamily: 'var(--loop-font-family-code, monospace)',
    background: 'var(--loop-neutral-100)',
    padding: '1px 4px',
    borderRadius: 'var(--loop-radius-xs)',
    fontSize: '0.85em',
  };

  return (
    <div style={panelStyle}>
      <div style={{ background: 'var(--loop-yellow-100, #fef9c3)', border: '1px solid var(--loop-yellow-400, #fbbf24)', borderLeft: '4px solid var(--loop-yellow-500, #f59e0b)', borderRadius: 'var(--loop-radius-sm)', padding: 'var(--loop-space-150) var(--loop-space-200)', fontSize: '0.875rem', color: 'var(--loop-neutral-1100)' }}>
        <strong>Before enabling DV on <code style={codeStyle}>{orgName}</code></strong>
        <p style={{ margin: 'var(--loop-space-075) 0 0 0', color: 'var(--loop-neutral-800)' }}>
          Without these prerequisites, DV verdict reviews will fall back to App auth and won&apos;t satisfy branch protection rules.
        </p>
      </div>

      <ol style={{ margin: 0, paddingLeft: 'var(--loop-space-300)', fontSize: '0.875rem', color: 'var(--loop-neutral-1000)', lineHeight: 1.6 }}>
        <li>
          <code style={codeStyle}>@diff-vader-bot</code> has been invited to <code style={codeStyle}>{orgName}</code> and has accepted the invite.
        </li>
        <li>
          <code style={codeStyle}>@diff-vader-bot</code> has been added to a write-access team in this org (via <code style={codeStyle}>terraform-rewindio-github</code>).
        </li>
      </ol>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--loop-space-100)', fontSize: '0.875rem', color: 'var(--loop-neutral-1000)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        I&apos;ve confirmed both prerequisites for <code style={{ ...codeStyle, marginLeft: 4 }}>{orgName}</code>
      </label>

      <div style={{ display: 'flex', gap: 'var(--loop-space-100)', justifyContent: 'flex-end', marginTop: 'var(--loop-space-050)' }}>
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={!confirmed || saving}
          onClick={onConfirm}
        >
          {saving ? 'Enabling…' : `Enable DV (${targetMode}) on ${orgName}`}
        </button>
      </div>
    </div>
  );
}

interface Props {
  title: string;
  initial: DvConfigShape;
  endpoint: string;
  onClose: () => void;
  onSaved: () => void;
  /** Org name — used to gate the prereq checklist when moving from disabled to shadow/enabled. */
  org?: string;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'var(--loop-space-300)',
  zIndex: 200,
  overflowY: 'auto',
};

const dialogStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  borderRadius: 'var(--loop-radius-md)',
  padding: 'var(--loop-space-300)',
  width: '100%',
  maxWidth: 400,
  marginTop: 'var(--loop-space-300)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 528,
  color: 'var(--loop-neutral-900)',
  marginBottom: 'var(--loop-space-075)',
};

const selectStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--loop-space-100) var(--loop-space-150)',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  background: 'var(--loop-neutral-0)',
  color: 'var(--loop-neutral-1100)',
  boxSizing: 'border-box',
};

export function ConfigEditModal({ title, initial, endpoint, onClose, onSaved, org }: Props) {
  const { idToken } = useAuth();
  const [mode, setMode] = useState<DvMode>(initial.mode);
  const [rerunOnPush, setRerunOnPush] = useState(initial.rerunOnPush);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When moving from disabled to shadow/enabled and org is known, show the prereq checklist before saving.
  const [showPrereqs, setShowPrereqs] = useState(false);

  const isEnablingFromDisabled =
    org !== undefined &&
    initial.mode === 'disabled' &&
    (mode === 'shadow' || mode === 'enabled');

  async function executeSave() {
    const parsed = DiffVaderConfigSchema.safeParse({ mode, rerunOnPush });
    if (!parsed.success) {
      setError(`Validation error: ${parsed.error.issues[0]?.message ?? 'invalid'}`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiSendJson(endpoint, 'PUT', idToken!, { mode, rerunOnPush });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.body);
      } else {
        setError((e as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  function handleSave() {
    if (isEnablingFromDisabled) {
      setShowPrereqs(true);
    } else {
      void executeSave();
    }
  }

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dialogStyle}>
        <h3 style={{ margin: '0 0 var(--loop-space-200)', fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>
          {title}
        </h3>

        {showPrereqs && org ? (
          <OrgEnablePrerequisites
            orgName={org}
            targetMode={mode as 'shadow' | 'enabled'}
            onConfirm={async () => { try { await executeSave(); } finally { setShowPrereqs(false); } }}
            onCancel={() => setShowPrereqs(false)}
            saving={saving}
          />
        ) : (
          <>
            <div style={{ marginBottom: 'var(--loop-space-150)' }}>
              <label style={labelStyle}>Mode</label>
              <select
                style={selectStyle}
                value={mode}
                onChange={(e) => setMode(e.target.value as DvMode)}
                disabled={saving}
              >
                <option value="disabled">disabled</option>
                <option value="shadow">shadow</option>
                <option value="enabled">enabled</option>
              </select>
            </div>

            <div style={{ marginBottom: 'var(--loop-space-200)', display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)' }}>
              <input
                type="checkbox"
                id="rerunOnPush"
                checked={rerunOnPush}
                onChange={(e) => setRerunOnPush(e.target.checked)}
                disabled={saving}
              />
              <label htmlFor="rerunOnPush" style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-900)', cursor: 'pointer' }}>
                Rerun on push
              </label>
            </div>

            {error && (
              <div style={{ color: 'var(--loop-red-700)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-150)', padding: 'var(--loop-space-100)', background: 'var(--loop-red-100)', borderRadius: 'var(--loop-radius-xs)' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--loop-space-100)', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
